'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

export default function ConsultantLoginPage() {
  const router = useRouter();
  const supabase = createClient();

  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const normalizeCode = (value: string) => {
    return value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedCode = code.trim().toUpperCase();
    const trimmedEmail = email.trim().toLowerCase();

    // Validate code format WB-ORG-XXXX
    if (!/^WB-ORG-[A-Z0-9]{4}$/.test(trimmedCode)) {
      setError('Format invalide. Le code doit être au format WB-ORG-XXXX (ex : WB-ORG-A3B7).');
      return;
    }

    if (!trimmedEmail) {
      setError('Veuillez saisir votre adresse email.');
      return;
    }

    setLoading(true);
    try {
      // Look up the consultant by code AND email (double verification)
      const { data: profile, error: lookupError } = await supabase
        .from('consultant_profiles')
        .select('id, email, status, consultant_code')
        .eq('consultant_code', trimmedCode)
        .maybeSingle();

      if (lookupError) throw lookupError;

      if (!profile) {
        throw new Error('Code d\'accès introuvable. Vérifiez votre code et réessayez.');
      }

      // Verify email matches the profile
      if (profile.email.toLowerCase() !== trimmedEmail) {
        throw new Error('Le code et l\'adresse email ne correspondent pas. Vérifiez vos informations.');
      }

      if (profile.status === 'suspended') {
        throw new Error('Votre accès a été suspendu. Contactez l\'administrateur.');
      }

      // Send OTP to the verified email
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: profile.email,
        options: {
          shouldCreateUser: false,
        },
      });

      if (otpError) throw otpError;

      // Redirect to OTP verification step
      router.push(`/consultant-login/verify?email=${encodeURIComponent(profile.email)}&code=${encodeURIComponent(trimmedCode)}`);
    } catch (err: any) {
      setError(err?.message || 'Erreur de connexion. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-40">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2.5">
            <AppLogo size={32} />
            <span className="font-bold text-foreground text-base">WBOSOrga</span>
          </Link>
          <Link
            href="/"
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5 rounded-lg hover:bg-muted"
          >
            <Icon name="ArrowLeftIcon" size={14} variant="outline" className="text-muted-foreground" />
            Retour à l&apos;accueil
          </Link>
        </div>
      </header>

      {/* Main */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md animate-slide-up">
          {/* Title */}
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="KeyIcon" size={28} variant="outline" className="text-primary" />
            </div>
            <h1 className="text-2xl font-extrabold text-foreground mb-2">Connexion Consultant</h1>
            <p className="text-sm text-muted-foreground">
              Saisissez votre code WB-ORG et votre adresse email pour accéder à votre espace
            </p>
          </div>

          {/* Form */}
          <div className="bg-card rounded-2xl border border-border p-6 card-shadow space-y-5">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Error */}
              {error && (
                <div className="bg-danger/10 border border-danger/20 rounded-xl p-3 flex items-start gap-2">
                  <Icon name="ExclamationCircleIcon" size={16} variant="solid" className="text-danger flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-danger">{error}</p>
                </div>
              )}

              {/* Code input */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  Code d&apos;accès consultant
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(normalizeCode(e.target.value))}
                  required
                  placeholder="WB-ORG-XXXX"
                  maxLength={11}
                  autoComplete="off"
                  spellCheck={false}
                  className="w-full bg-input border border-border rounded-xl px-4 py-3 text-center text-xl font-black tracking-[6px] text-foreground placeholder:text-muted-foreground/40 placeholder:tracking-normal placeholder:text-base placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  Format : WB-ORG-XXXX — reçu par email lors de la création de votre compte
                </p>
              </div>

              {/* Email input */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  Adresse email associée au compte
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="votre@email.com"
                  autoComplete="email"
                  className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  Un code de vérification vous sera envoyé à cette adresse
                </p>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading || code.length < 11 || !email.trim()}
                className="w-full flex items-center justify-center gap-2 bg-primary text-white font-semibold text-sm px-6 py-3.5 rounded-xl hover:bg-primary/90 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Vérification…
                  </>
                ) : (
                  <>
                    <Icon name="EnvelopeIcon" size={18} variant="outline" className="text-white" />
                    Recevoir le code de vérification
                  </>
                )}
              </button>
            </form>

            {/* Info */}
            <div className="bg-info-bg border border-info/20 rounded-xl p-4">
              <p className="text-xs font-bold text-info mb-1.5 flex items-center gap-1.5">
                <Icon name="InformationCircleIcon" size={14} variant="solid" className="text-info" />
                Double vérification
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Votre identité est vérifiée en deux étapes : <span className="font-semibold text-foreground">code WB-ORG</span> + <span className="font-semibold text-foreground">email</span>, puis un <span className="font-semibold text-foreground">OTP à 6 chiffres</span> envoyé à votre adresse.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
